import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';

import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '@services/auth/auth.service';
import { LanguageService } from '@services/language/language.service';
import { ToastService } from '@services/toast/toast.service';
import { ModalFormField, ModalService } from '@services/modal/modal.service';
import { ApiSysService } from '@services/api/api-sys.service';
import { LoadingService } from '@services/loading/loading.service';

@Component({
    selector: 'app-user-menu',
    imports: [RouterModule],
    templateUrl: './user-menu.component.html',
    styleUrl: './user-menu.component.scss'
})
export class UserMenuComponent implements OnInit, OnDestroy {
  @ViewChild('dropdownRef', { static: false }) dropdownRef!: ElementRef;

  isDropdownOpen = false;
  isImagePreviewOpen = false;
  translateData: any = {};
  userName = '';
  userSurname = '';
  userOrg = '';
  userAvatar: string | null = '';

  isPilot = false;
  isManager = false;

  categories: any[] = [
    { id: 'landing', name: 'Landing' },
    { id: 'coordination-list', name: 'Coordination list' },
    { id: 'document', name: 'Document' },
    { id: 'monitor', name: 'Monitor' }
  ];

  infrastructures: any[] = [];

  private destroy$ = new Subject<void>();
  private apiSys = inject(ApiSysService);

  constructor(
    private authService: AuthService,
    private langService: LanguageService,
    private toastService: ToastService,
    private modalService: ModalService,
    private router: Router,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.loadTranslations();
    this.loadInfrastructures();
    this.loadProfilePicture();

    const personnel = this.authService.getUser();
    if (personnel) {
      this.userName = personnel.per_name || 'User';
      this.userSurname = personnel.per_surname || '';
      this.userOrg = personnel.org_name || 'Organization';

      this.isPilot = this.authService.isPilot();
      this.isManager = this.authService.isManager();
    } else {
      this.userName = 'Guest';
      this.userSurname = '';
      this.userOrg = '';
    }

    this.authService.profilePictureUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadProfilePicture();
      });

    this.authService.profileUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const user = this.authService.getUser();
        this.userName = user?.per_name || 'User';
        this.userSurname = user?.per_surname || '';
        this.userOrg = user?.org_name || 'Organization';
        this.loadProfilePicture();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  get userId(): string {
    return this.authService.getUserId()?.toString() || '';
  }

  get orgId(): number {
    const user = this.authService.getUser();
    return user?.org_id || 0;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isDropdownOpen && this.dropdownRef) {
      const clickedInside = this.dropdownRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeDropdown();
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.isImagePreviewOpen) {
      this.closeImagePreview();
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  openImagePreview(event: Event): void {
    event.stopPropagation();
    if (this.userAvatar) {
      this.isImagePreviewOpen = true;
      document.body.style.overflow = 'hidden';
    }
  }

  closeImagePreview(): void {
    this.isImagePreviewOpen = false;
    document.body.style.overflow = '';
  }

  private async loadProfilePicture(): Promise<void> {
    try {
      this.userAvatar = await this.authService.getUserProfilePictureUrl();
    } catch (error) {
      this.userAvatar = '';
    }
  }

  private loadTranslations(): void {
    this.langService.getSharedDictionary('user-menu')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.translateData = data;
      });
  }

  private async loadInfrastructures(): Promise<void> {
    // Don't load infrastructures if user is not logged in
    if (!this.authService.getToken() || !this.authService.getUser()) {
      return;
    }

    try {
      const response = await this.apiSys.getInfrastructures(this.authService.getToken());

      if (response.data) {
        this.infrastructures = response.data.data || response.data;
      } else if (response.error) {
        this.toastService.error(this.translateData?.error, this.translateData?.failed_to_load);
      }
    } catch (error) {
      this.toastService.error(this.translateData?.error, this.translateData?.failed_to_load);
    }
  }

  navigateToProfile(): void {
    this.closeDropdown();
    this.router.navigate([`/resources/personnel/${this.authService.getUserId()}`]);
  }

  navigateToSettings(): void {
    this.closeDropdown();
    this.openSettingsModal();
  }

  navigateToOrganization(): void {
    this.closeDropdown();
    this.router.navigate([
      '/resources/organization',
      this.authService.getUser()?.org_id,
    ]);
  }

  navigateToPetitions(): void {
    this.closeDropdown();
    this.router.navigate(['/petitions']);
  }

  private async openSettingsModal(): Promise<void> {
    this.loadingService.startLoading('atly-web');

    try {
      const userId = this.authService.getUserId();
      const currentPreferences = await firstValueFrom(
        this.apiSys.getPersonnelPreferences(userId.toString())
      );

      const { defaultPage, infrastructureId } = this.parsePreferredUrl(currentPreferences?.preferred_url);
      const formFields = this.createFormFields(defaultPage, infrastructureId);

      const modalOptions = {
        backdropClose: false,
        customClass: 'settings-modal',
        buttons: [
          {
            text: () => this.translateData?.cancel || 'Cancel',
            value: false,
            close: true,
            class: 'modal__button modal__button--secondary'
          },
          {
            text: () => this.translateData?.save || 'Save Settings',
            value: true,
            primary: true,
            close: true,
            class: 'modal__button modal__button--primary'
          }
        ]
      };

      this.loadingService.stopLoading('atly-web');

      this.modalService.form(formFields, () => this.translateData?.settings || 'Settings', modalOptions).subscribe({
        next: (result) => {
          if (result && result.value !== false) {
            this.handleSettingsModalResult(result);
          }
        }
      });
    } catch (error) {
      this.loadingService.stopLoading('atly-web');
      const formFields = this.createFormFields();

      const modalOptions = {
        customClass: 'settings-modal',
        backdropClose: false,
        buttons: [
          {
            text: () => this.translateData?.cancel || 'Cancel',
            value: false,
            close: true,
            class: 'modal__button modal__button--secondary'
          },
          {
            text: () => this.translateData?.save || 'Save Settings',
            value: true,
            primary: true,
            close: true,
            class: 'modal__button modal__button--primary'
          }
        ]
      };

      this.modalService.form(formFields, () => this.translateData?.settings || 'Settings', modalOptions).subscribe({
        next: (result) => {
          if (result && result.value !== false) {
            this.handleSettingsModalResult(result);
          }
        }
      });
    }
  }

  private parsePreferredUrl(preferredUrl?: string): { defaultPage: string | null, infrastructureId: string | null } {
    if (!preferredUrl) {
      return { defaultPage: null, infrastructureId: null };
    }

    const urlParts = preferredUrl.replace(/^\//, '').split('/');
    const defaultPage = urlParts[0] || null;
    let infrastructureId = null;

    if (urlParts.length > 1 && urlParts[1]) {
      const infrastructureCode = urlParts[1];
      const infrastructure = this.infrastructures.find(infra =>
        infra.str_code === infrastructureCode || infra.str_id === infrastructureCode
      );

      if (infrastructure) {
        infrastructureId = infrastructure.str_id;
      }
    }

    return { defaultPage, infrastructureId };
  }

  private createFormFields(currentDefaultPage?: string | null, currentInfrastructureId?: string | null): ModalFormField[] {
    const fields: ModalFormField[] = [
      {
        name: 'default_page',
        label: this.translateData?.default_page || 'Default page',
        type: 'select',
        value: currentDefaultPage || '',
        placeholder: this.translateData?.select_default_page || 'Select a default page',
        required: true,
        options: this.getDefaultPageOptions()
      }
    ];

    const hasInfrastructures = Array.isArray(this.infrastructures)
      ? this.infrastructures.length > 0
      : Object.keys(this.infrastructures || {}).length > 0;

    if (hasInfrastructures) {
      fields.push({
        name: 'infrastructure',
        label: this.translateData?.infrastructure || 'Infrastructure',
        type: 'select',
        value: currentInfrastructureId || '',
        placeholder: this.translateData?.select_infrastructure || 'Select an infrastructure',
        required: false,
        options: this.getInfrastructureOptions(),
        showWhen: [
          {
            field: 'default_page',
            value: 'document',
            operator: 'eq'
          },
          {
            field: 'default_page',
            value: 'monitor',
            operator: 'eq'
          }
        ]
      });
    }

    return fields;
  }

  private getDefaultPageOptions(): { value: any, label: string }[] {
    const hasInfrastructures = Array.isArray(this.infrastructures)
      ? this.infrastructures.length > 0
      : Object.keys(this.infrastructures || {}).length > 0;

    let availableCategories = [...this.categories];

    if (!hasInfrastructures) {
      availableCategories = availableCategories.filter(page =>
        !['document', 'monitor'].includes(page.id)
      );
    }

    if (!this.isPilot && !this.isManager) {
      availableCategories = availableCategories.filter(page =>
        page.id !== 'coordination-list'
      );
    }

    const options = availableCategories.map(page => ({
      value: page.id,
      label: page.name
    }));

    return options;
  }

  private getInfrastructureOptions(): { value: any, label: string }[] {
    if (Array.isArray(this.infrastructures)) {
      return this.infrastructures.map(infra => ({
        value: infra.str_id,
        label: infra.str_denom
      }));
    } else if (this.infrastructures && typeof this.infrastructures === 'object') {
      return Object.values(this.infrastructures).map((infra: any) => ({
        value: infra.str_id,
        label: infra.str_denom
      }));
    }

    return [];
  }

  private async handleSettingsModalResult(result: any): Promise<void> {
    const selectedPage = result.value.default_page;
    const selectedInfrastructureId = result.value.infrastructure;

    if (!selectedPage) {
      this.toastService.error(this.translateData?.error, this.translateData?.select_default_page);
      return;
    }

    const categoriesWithInfrastructure = ['document', 'monitor'];
    if (categoriesWithInfrastructure.includes(selectedPage) && !selectedInfrastructureId) {
      this.toastService.error(this.translateData?.error, this.translateData?.select_infrastructure);
      return;
    }

    let preferred_url = `/${selectedPage}`;

    if (categoriesWithInfrastructure.includes(selectedPage) && selectedInfrastructureId) {
      const selectedInfrastructure = this.infrastructures.find(infra =>
        infra.str_id == selectedInfrastructureId
      );

      if (selectedInfrastructure) {
        preferred_url = `/${selectedPage}/${selectedInfrastructure.str_id}`;
      } else {
        preferred_url = `/${selectedPage}/${selectedInfrastructureId}`;
      }
    }

    const preferences = { preferred_url };

    this.loadingService.startLoading('atly-web');
    try {
      const userId = this.authService.getUserId();
      await firstValueFrom(
        this.apiSys.updatePersonnelPreferences(userId.toString(), preferences)
      );

      if (selectedInfrastructureId) {
        localStorage.setItem('strId', selectedInfrastructureId);
      }

      this.loadingService.stopLoading('atly-web');
      this.toastService.success(
        this.translateData?.success || 'Success',
        this.translateData?.settings_saved || 'Settings saved successfully'
      );
    } catch (error) {
      this.loadingService.stopLoading('atly-web');
      this.toastService.error(this.translateData?.error, this.translateData?.failed_to_save);
    }
  }
}